import * as React from "react";
import {
	Box, Checkbox, Chip, CircularProgress, FormControl,
	Grid, InputLabel, ListItemText, MenuItem,
	Paper, TextField, Typography,
} from "@mui/material";
import {Autorenew, ErrorOutline, Visibility} from "@mui/icons-material";
import {translate} from "../../../translate/translate";
import {useParams} from "react-router-dom";
import {FtBomService} from "../../../services/ft-bom-service";
import {useEffect, useState} from "react";
import ReuseFtbMaterialComponent from "./reuse-ftb-material.component";
import IconButton from "@mui/material/IconButton";
import {MountJiga, MountJigaGroup, originOptions, ReuseFtbMaterialOrigin} from "../../../models/basic/ft-bom";
import {ErrorComponent} from "../../../components/error/error.component";
import TableActionComponent from "../../../components/table/table-action.component";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import Select from "@mui/material/Select";
import CollapsibleTableComponent from "../../../components/table/collapsible-table.component";

type mountJigaProps = {
    doneMount: (done: boolean) => void;
    changeActive: (done: boolean) => void;
	hasPermission: boolean;
}

const MountJigaComponent: React.FC<mountJigaProps> = ({
    doneMount,
    changeActive,
	hasPermission,
}) => {
    const params = useParams();
    const idClient = params.idClient;
    const ftbom = params.ftbom;

    const service = new FtBomService();
    const [loading, setLoading] = useState(false);
    const [dataSourceMountJigaGroups, setDataSourceMountJigaGroups] = React.useState<MountJigaGroup[]>();

    const [groupsSearch, setGroupsSearch] = useState<string[]>([]);
    const [selectedGroupSearch, setSelectedGroupSearch] = useState<string[]>([]);
    const [quantityJiga, setQuantityJiga] = useState<number>(0);

    const [openDialogReuse, setOpenDialogReuse] = useState<boolean>(false);
    const [selectedMountDialog, setSelectedMountDialog] = useState<MountJiga | MountJigaGroup | undefined>(undefined);
	const [selectedTotalGroupDialog, setSelectedTotalGroupDialog] = useState<number | undefined>(undefined);
    const [visualization, setVisualization] = useState<boolean>(false);
    const [search, setSearch] = useState<string>()

	const [openedDetail, setOpenedDetail] = React.useState<{ [id: string]: boolean }>({});
	const [loadingDetail, setLoadingDetail] = React.useState<{ [id: string]: boolean }>({});

	const [listDetail, setListDetail] = React.useState<{ [id: string]: MountJiga[] }>({});
	const [listDetailSelected, setListDetailSelected] = React.useState<{ [id: string]: MountJiga[] }>({});
	const [listDetailAux, setListDetailAux] = React.useState<{ [id: string]: MountJiga[] }>({});
	const [quantitySented, setQuantitySent] = useState<{[key: number]: number}>();

    useEffect(() => {
		loadMountJigaGroups();
    }, []);

    useEffect(() => {
		if (selectedGroupSearch.length > 0 || !!search) {
			loadMountJigaGroups(false);
		}
    }, [selectedGroupSearch, search]);

	const loadMountJigaGroups = (initialize = true) => {
		if (idClient && ftbom) {
			setLoading(true);
			service.get_mount_jiga_groups(idClient, ftbom, selectedGroupSearch, search).then((response) => {
				setDataSourceMountJigaGroups(response.data);
				setQuantityJiga(response.data && response.data.length > 0 ? response.data[0].quantity_jiga : 0);

				if (initialize) {
					const groups = response.data?.map((it: any) => it.alternative_group_bom);
					// @ts-ignore
					const uniqueGroups = [...new Set(groups)];
					setGroupsSearch(uniqueGroups);
					if (!allSearchGroupSelected()) {
						setSelectedGroupSearch(uniqueGroups);
					}
				}
			}).catch((error: any) => {
				ErrorComponent(error);
			})
			.finally(() => {
				setLoading(false);
			});
		}
	}

    const changeSelectedMountJiga = (mountJiga: MountJiga, valueQtyOrigin?: number) => {
		const isSelected = listDetailSelected[mountJiga.alternative_group_bom].find((item) => item.id === mountJiga.id);
		const mount = mountJiga;
		let listFiltered = listDetailSelected[mountJiga.alternative_group_bom];
		if (isSelected) {
			mount.used_in_jiga = false;
			mount.quantity_origin_jiga = quantitySented?.[mountJiga.id] || 0;
			mount.qty_reused = 0;
			listFiltered = listFiltered.filter((item) => item.id !== mountJiga.id);
			setQuantitySent((prev: any) => ({ ...prev, [mountJiga.id]: 0 }));
		} else {
			mount.used_in_jiga = true;
			mount.quantity_origin_jiga = valueQtyOrigin || quantitySented?.[mountJiga.id] || 0;
			listFiltered = [...listFiltered, mountJiga];
			setQuantitySent((prev: any) => ({ ...prev, [mountJiga.id]: valueQtyOrigin ?? quantitySented?.[mountJiga.id] ?? mountJiga.quantity_origin_jiga }));
		}
		saveMountJiga([mount]);

		setListDetailSelected({...listDetailSelected, [mountJiga.alternative_group_bom]: listFiltered});
		updateSelectGroup(mountJiga, listFiltered);
    };

	const updateSelectGroup = (mountJiga: MountJiga, listFiltered: MountJiga[], arrQuantity?: any) => {
		let soma = 0;
		let selected = false;
		if (arrQuantity !== undefined) {
			soma = getSumItems(listFiltered, arrQuantity);
			selected = listFiltered && listFiltered.length > 0 && !listFiltered.some(item => checkIfIsZero(arrQuantity, item)) && soma == mountJiga.total_jiga;
			updateOrigin(mountJiga, arrQuantity);
		} else {
			soma = getSumItems(listFiltered, quantitySented);
			selected = listFiltered && listFiltered.length > 0 && !listFiltered.some(item => checkIfIsZero(quantitySented, item)) && soma == mountJiga.total_jiga;
			updateOrigin(mountJiga, quantitySented);
		}
		const updatedItems = dataSourceMountJigaGroups?.map((item) =>
			item.alternative_group_bom === mountJiga.alternative_group_bom
				? { ...item, selected: selected }
				: item
		);
		setDataSourceMountJigaGroups(updatedItems);
	}

	const getSumItems = (arrToSum: MountJiga[], arrQuantity: any) => {
		return arrToSum.reduce((acc, key) => {
			const quantity = !!arrQuantity && arrQuantity[key.id] !== undefined ? arrQuantity[key.id] : (key.quantity_origin_jiga || 0);
			const quantity_reused = key.qty_reused || 0;
			return acc + quantity + quantity_reused;
		}, 0);
	}

	const updateOrigin = (mountJiga: MountJiga, arrayQtde: any) => {
		const qty_origin = arrayQtde && arrayQtde[mountJiga.id] ? arrayQtde[mountJiga.id] : (mountJiga.quantity_origin_jiga || 0);
		const qty_reused = mountJiga.qty_reused || 0;
		let origin = originOptions.filter(it => it.id == ReuseFtbMaterialOrigin.ESTOQUE)[0].id;
		if (qty_origin !== 0 && qty_reused !== 0) {
			origin = originOptions.filter(it => it.id == ReuseFtbMaterialOrigin.MISTO)[0].id;
		} else if (qty_reused !== 0) {
			origin = originOptions.filter(it => it.id == ReuseFtbMaterialOrigin.REUTILIZADO)[0].id;
		}
		const listaAtualizada = listDetail[mountJiga.alternative_group_bom].map( item => {
			if (item.id == mountJiga.id) {
				return {...item, origin: origin};
			} else {
				return item;
			}
		});
		setListDetail({...listDetail, [mountJiga.alternative_group_bom]: listaAtualizada});
	}

    const saveMountJiga = (mountJiga: MountJiga[]) => {
        service.save_mount(mountJiga, 'jiga')
            .then((response) => {
                doneMount(response.data.status_mount);
            })
            .catch((error: any) => {
                ErrorComponent(error);
            })
    }

	const checkIfIsZero = (quantitySented: any, row: MountJiga) => {
		return ((quantitySented && quantitySented[row.id] == 0 && row.qty_reused == 0) || (!quantitySented?.[row.id] && row.quantity_origin_jiga == 0) && row.qty_reused == 0);
	}

	const validateQuantityOrigin = (row: MountJiga) => {
        if(quantitySented && quantitySented?.[row.id] <= 0 ) {
            return false;
        }

        if(row.quantity_origin_jiga && row.quantity_origin_jiga <= 0) {
            return false;
        }

		const ids = listDetailSelected[row.alternative_group_bom]?.map(it => it.id);
		if ( !row.used_in_jiga && !ids?.includes(Number(row.id))) {
			return false;
		}

		const soma_origin = listDetailSelected[row.alternative_group_bom]?.reduce((acc, key) => acc + (quantitySented && quantitySented[key.id] ? quantitySented[key.id] : (key.quantity_origin_jiga || 0)), 0);
        const soma_reused = listDetailSelected[row.alternative_group_bom]?.map((key) => (key.qty_reused || 0)).reduce((acc, key) => acc + key);
        const soma = soma_origin + soma_reused
		return soma !== row.total_jiga;
	}

	const QuantityInputCell = React.memo(({ row, index, value, setQuantitySent, validateQuantityOrigin, saveMountJiga }: any) => {

		const [localValue, setLocalValue] = useState(
			quantitySented?.[row.id] ?? row.quantity_origin_jiga
		);

		const handleChange = (event: any) => {
			const parsed = parseInt(event.target.value, 10);
			if (parsed < 0 || isNaN(parsed) || event.target.value.length > 10) return;
			setLocalValue(parsed);
		};

		const handleBlur = () => {
			setQuantitySent((prev: any) => ({ ...prev, [row.id]: localValue }));
			const mount = { ...row, quantity_origin_jiga: localValue };
			saveMountJiga([mount]);
			updateSelectGroup(mount, listDetailSelected[mount.alternative_group_bom], {...quantitySented, [row.id]: localValue });

			if (localValue > 0) {
				const isSelected = listDetailSelected[mount.alternative_group_bom].find((item) => item.id === mount.id);
				!isSelected && changeSelectedMountJiga(mount, localValue);
			}
		};

		return (
			<TextField
				key={row.id}
				name="quantity_origin_jiga"
				variant="outlined"
				fullWidth
				type="number"
				value={localValue}
				error={validateQuantityOrigin(row)}
				onChange={handleChange}
				onBlur={handleBlur}
				disabled={!row.active || !hasPermission}
				inputProps={{
					sx: { maxHeight: "0px !important", height: "0px !important" }
				}}
                title={validateQuantityOrigin(row) ? "A soma das quantidades deve ser igual ao total" : ""}
			/>
		);
	});

    const customCellRender = (column: string, index: number, row: MountJiga) => {
        if (column === "used_in_jiga") {
            const isSelected = listDetailSelected[row.alternative_group_bom].some((item) => item.id === row.id);
            return (
                <Checkbox
                    checked={isSelected}
                    onChange={() => changeSelectedMountJiga(row)}
                    disabled={!row.active || !hasPermission}
                />
            );
        }
        if (column === "origin") {
            const origin = originOptions.find(option => option.id === row.origin);
            return (
                <Chip size="small"
                      color={origin!.color as any}
                      label={origin!.label}
                />
            );
        }
        if (column === "reuse") {
            if (row.active && row.can_reuse_material) {
                return (
                    <IconButton
                        onClick={() => {
                            setSelectedMountDialog(row);
							const group = listDetailSelected[row.alternative_group_bom];
							const sum = group.reduce((acc, key) => acc + (quantitySented && quantitySented[key.id] ? quantitySented[key.id] : (key.quantity_origin_jiga || 0)), 0);
							setSelectedTotalGroupDialog(sum);
                            setOpenDialogReuse(true)
                        }}
						disabled={!hasPermission}
                    >
                        <Autorenew/>
                    </IconButton>
                )
            }
            return "";
        }
        if (column === "can_reuse_material") {
            if (row.can_reuse_material) {
                return <Box className="blue-marker"></Box>
            }
            return "";
        }
		if (column === "qty_origin") {
			return (
				<QuantityInputCell
					row={row}
					index={index}
					value={quantitySented?.[row.id] ?? row.quantity_origin_jiga}
					setQuantitySent={setQuantitySent}
					validateQuantityOrigin={validateQuantityOrigin}
					saveMountJiga={saveMountJiga}
				/>
			);
		}
        return "-";
    };

	const customCellGroupRender = (column: string, index: number, row: MountJigaGroup) => {
		if (column === "alternative_group_bom") {
			return (
				<Typography variant="body2" color="textSecondary"
							sx={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}
				>
					<ErrorOutline sx={{color: !row.selected ? 'orange': 'transparent', verticalAlign: 'middle', display: row.selected ? 'none' : 'inline'}}/>
					{row.alternative_group_bom || "-"}
				</Typography>
			);
		}
		if (column === "reused") {
			return (row.has_reused ?
				(<Box display={"flex"} alignItems="left" justifyContent="left" whiteSpace="nowrap">
					<IconButton
						onClick={() => {
							setVisualization(true);
							setSelectedMountDialog(row);
							setOpenDialogReuse(true)
						}}
						color="primary">
						<Visibility/>
					</IconButton>
					</Box>
				) : (""))
		}
		if (column === "can_reuse_material") {
			if (row.can_reuse_material) {
				return <Box className="blue-marker"></Box>
			}
			return "";
		}
		return "";
	};

    const notSelectedGroups = () => {
        const groups = dataSourceMountJigaGroups?.map(it => it.alternative_group_bom);
		const groupsSelected = dataSourceMountJigaGroups?.filter(it => it.selected).map(it => it.alternative_group_bom);
        // @ts-ignore
        const uniqueGroups = [...new Set(groups)];
        const uniqueGroupsSelected = new Set(groupsSelected);
        return uniqueGroups.filter(group => !uniqueGroupsSelected.has(group));
    }

    const allSearchGroupSelected = () => {
        return groupsSearch.length === selectedGroupSearch.length;
    }

    const allPendingGroupsSelected = () => {
        if (notSelectedGroups().length === 0) return false;
        if (selectedGroupSearch.length === 0) return false;
        if (notSelectedGroups().length !== selectedGroupSearch.length) return false;

        const sortedA = [...notSelectedGroups()].sort();
        const sortedB = [...selectedGroupSearch].sort();

        return sortedA.every((val, index) => val === sortedB[index]);
    }

    const renderSelection = (selected: any, dataSource: any[], allSelected: () => boolean) => {
        if (allSelected()) {
            return translate.t('all');
        }

        if (allPendingGroupsSelected()) {
            return translate.t('pending_groups');
        }

        const sorted = selected
            .map((it: any) => dataSource.find(item => item === it))
            .filter(Boolean)
            .sort()

        return sorted.join(", ");
    };

	const loadDetail = (row: any) => {
		setOpenedDetail({...openedDetail, [row.alternative_group_bom]: !openedDetail[row.alternative_group_bom]});

		if (!openedDetail[row.alternative_group_bom]) {
			updateDetail(row);
		}
	}

	const updateDetail = (row: any)=> {
		setListDetail({...listDetail, [row.alternative_group_bom]: []});
		setLoadingDetail({...loadingDetail, [row.alternative_group_bom]: true});
		const filter = { client_id: Number(idClient), ft_bom: ftbom!!, alternative_group_bom: row.alternative_group_bom, search: search };
		service.get_mount_jiga_group_children(filter).then(response => {
			const allMounts = response.data;
			setListDetail({...listDetail, [row.alternative_group_bom]: allMounts});
			setListDetailAux({...listDetailAux, [row.alternative_group_bom]: allMounts});
			const selected = allMounts.filter((mountJiga: MountJiga) => mountJiga.used_in_jiga);
			setListDetailSelected({...listDetailSelected, [row.alternative_group_bom]: selected});
		}).catch((error: any) => {
			ErrorComponent(error);
		}).finally(() => {
			setLoadingDetail({...loadingDetail, [row.alternative_group_bom]: false});
		});
	}

	const layoutDetail = (row: any) => {
		if (loading) {
			return ""
		}
		if (loadingDetail[row.alternative_group_bom]) {
			return <Box padding={1}>
				<CircularProgress size="30px" />
			</Box>
		}
		return (
			<TablePaginationComponent
				key={row.alternative_group_bom}
				state={listDetail[row.alternative_group_bom] || []}
				displayedColumnsShow={["", "", "Material Code", "Description EN", "Description PT", "Origin", "Qty Origin", "Qty Reuse", "Reused from"]}
				displayedColumns={["used_in_jiga", "can_reuse_material", "foxconn_part_number", "description_english", "description_portuguese", "origin", "qty_origin", "qty_reused", "reuse"]}
				columnStyles={{1:"5%", 2:"1%", 3:"14%", 4:"20%", 5:"20%", 6: "10%", 7:"10%", 8:"10%", 9:"10%"}}
				numberColumns={["usage", "total_jiga"]}
				integerColumns={["usage", "total_jiga"]}
				actionsComponent={TableActionComponent}
				loading={loading}
				customColumns={["can_reuse_material", "used_in_jiga", "reuse", "ft_bom", "origin", "alternative_group_bom", "qty_origin"]}
				customCellRender={customCellRender}
			/>
		)
	}

	const openDetail = (row: any) => {
		setOpenedDetail({...openedDetail, [row.alternative_group_bom]: !openedDetail[row.alternative_group_bom]});
		loadDetail(row);
	}

    return (
        <>
            <Box sx={{display: "flex", flexDirection: "column", gap: "20px"}}>
				<Typography>
					<b>Qtde. de Jiga para montagem:</b> {quantityJiga} unidade(s)
				</Typography>

				<Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
					<Box sx={{display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap"}}>
						<Grid width="250px">
							<FormControl fullWidth>
								<InputLabel>
									Pesquisar por grupo de material
								</InputLabel>
								<Select
									name="group"
									label={"Pesquisar por grupo de material"}
									variant={"outlined"}
									fullWidth
									aria-placeholder="Pesquisar por grupo de material"
									autoFocus={false}
									value={selectedGroupSearch}
									onChange={(event: any) => {
										if (event.target.value.includes(0)) {
											if (!allSearchGroupSelected()) {
												setSelectedGroupSearch(groupsSearch.map((p: string) => p));
												return;
											}
											setSelectedGroupSearch([]);
											return;
										}

										if (event.target.value.includes(9999)) {
											if (!allPendingGroupsSelected()) {
												setSelectedGroupSearch(notSelectedGroups());
												return;
											}
											setSelectedGroupSearch([]);
											return;
										}

										setSelectedGroupSearch(event.target.value)
									}}
									multiple
									renderValue={(selected) => renderSelection(selected, groupsSearch, allSearchGroupSelected)}
									MenuProps={{
										PaperProps: {
											style: {
												maxHeight: '400px',
											},
										},
										disableAutoFocusItem: true
									}}
									inputProps={{
										sx: {
											maxWidth: '400px !important',
											width: '400px !important',
										}
									}}
								>
									<MenuItem key={0} value={0} disabled={loading}>
										<Checkbox checked={allSearchGroupSelected()}/>
										<ListItemText primary={
											<Box display="flex" alignItems="center" justifyContent="space-between">
												<Typography variant="body1">{translate.t('all')}</Typography>
												{allSearchGroupSelected() && <Typography variant="body2"
                                                                                         color="gray">{translate.t('clear')}</Typography>}
											</Box>
										}/>
									</MenuItem>
									{notSelectedGroups().length && (<MenuItem key={9999} value={9999} disabled={loading}>
										<Checkbox checked={allPendingGroupsSelected()}/>
										<ListItemText primary={
											<Box display="flex" alignItems="center" justifyContent="space-between">
												<Typography variant="body1">Grupos pendentes</Typography>
												{allPendingGroupsSelected() && <Typography variant="body2"
                                                                                           color="gray">{translate.t('clear')}</Typography>}
											</Box>
										}/>
									</MenuItem>)}
									{groupsSearch?.map((p: any) => (
										<MenuItem key={p} value={p} disabled={loading}>
											<Checkbox checked={selectedGroupSearch.includes(p)}/>
											<ListItemText primary={p}/>
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid width="280px">
							<FormControl fullWidth>
								<TextField
									sx={{minWidth: "150px !important"}}
									name="search"
									label={""}
									fullWidth
									variant={"outlined"}
									autoFocus={false}
									value={search}
									placeholder="Pesquisar por material code e description"
									onChange={(event: React.ChangeEvent<{
										value: string
									}>) => {
										setSearch(event.target.value);
									}}
								/>
							</FormControl>
						</Grid>
					</Box>
					<Box sx={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-end", flexWrap: "wrap", marginRight:"10px"}}>
						<Grid className="blue-marker"></Grid>
						<Grid className="blue-text">Material disponível para reuso</Grid>
					</Box>
				</Box>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "16px"}}>
                <Typography style={{marginTop: "16px"}}>
                    Selecione os materiais que devem ser utilizados na montagem da Jiga:
                </Typography>
                <Typography style={{marginTop: "16px", alignItems: "center", display: "flex", marginRight: "10px"}}>
                    <ErrorOutline sx={{color: 'orange', marginBottom: '0.2rem', marginRight: '0.4rem'}}/>
                    <b>Grupos pendentes: &ensp;</b> {notSelectedGroups().length}
                </Typography>
            </Box>
            <Paper variant="outlined" sx={{marginTop: "16px"}}>
				<CollapsibleTableComponent
					state={dataSourceMountJigaGroups}
					displayedColumnsShow={["", "", "Alt Grp BoM", "Qty Usage", "Qty Total", "Reused"]}
					displayedColumns={["group", "can_reuse_material", "alternative_group_bom", "usage", "total", "reused"]}
					numberColumns={["usage", "total"]}
					integerColumns={["usage", "total"]}
					columnStyles={{1:"5%", 2: "1%", 3:"64%", 4:"10%", 5:"10%", 6:"10%"}}
					actionsComponent={TableActionComponent}
					childrenComponent={layoutDetail}
					loading={loading}
					loadOnOpen={openDetail}
					openControl={openedDetail}
					openKey={"alternative_group_bom"}
					customCellRender={customCellGroupRender}
					customColumns={["can_reuse_material", "group", "alternative_group_bom", "reused"]}
				/>
            </Paper>

            <ReuseFtbMaterialComponent
                openDialog={openDialogReuse}
                mountJiga={selectedMountDialog}
                visualization={visualization}
				idClient={idClient!!}
				totalGroup={selectedTotalGroupDialog}
                onClose={(save) => {
	                if (!visualization && save) {
		                updateDetail(selectedMountDialog);
	                }
                    setOpenDialogReuse(false);
                    setSelectedMountDialog(undefined);
					setSelectedTotalGroupDialog(undefined);
                    setVisualization(false);
                }}
            />
        </>
    );
};

export default MountJigaComponent;
